import { Plan } from '@accedo/one-plugin-sdk';
import { ReactNode } from 'react';

import {
  FormInputTypes,
  InputApiField,
  SubmitResponse,
  TFormData,
} from '#/client/models';
import { FormInputVariants } from '#/service/constants';

export enum InputGroupType {
  Field = 'field',
  Plan = 'plan',
  Button = 'button',
}

export enum FormAction {
  Update = 'update',
  Error = 'error',
  Reset = 'reset',
  PlanChange = 'planChange',
}

export interface InputBase {
  key: string;
  type: FormInputTypes;
}

export interface InputVariantBase extends InputBase {
  type: FormInputTypes.INPUT | FormInputTypes.STATIC;
  variant: FormInputVariants;
}

export interface InputPlanBase extends InputBase {
  type: FormInputTypes.PLAN | FormInputTypes.STATIC;
  id: string;
}

export interface InputField extends InputVariantBase, InputApiField {
  variant: Exclude<
    FormInputVariants,
    FormInputVariants.BUTTON | FormInputVariants.SUBMIT
  >;
  defaultValue?: string;
  description?: string;
  validations?: TValidation[];
  help?: ReactNode;
  format?: string;
  values?: { key: string; value: string }[]; // For select and radio inputs
  width?: string; // CSS width value (e.g., '50%', '100%', '200px')
}

export interface InputPlan extends Plan, InputPlanBase {
  isDisabled?: boolean;
}

export interface InputButtonBase extends InputVariantBase {
  text: string;
  onClick: Function;
  disclaimer?: string;
}

export interface InputButtonRegular extends InputButtonBase {
  variant: FormInputVariants.BUTTON;
  onClick: (formData: TFormData, buttonText: string) => void;
  isMainButton?: boolean;
}

export interface InputButtonSubmit extends InputButtonBase {
  variant: FormInputVariants.SUBMIT;
  onClick: (formData: TFormData, buttonText: string) => Promise<SubmitResponse>;
  isMainButton?: false;
}

export type TValidation = {
  message?: string;
  regexp?: string;
  match?: string;
  remote?: boolean;
  timestamp?: {
    min?: string;
    max?: string;
  };
};

export type TInputState<T = string | boolean> = {
  touched?: boolean;
  value?: T;
};

export type InputGroup = InputFieldGroup | InputPlanGroup | InputButtonGroup;

export type TInputGroup<
  TInputGroupType extends InputGroupType,
  TInputs extends any[],
> = {
  key: string;
  type: TInputGroupType;
  inputs: TInputs;
};

export type InputFieldGroup = TInputGroup<InputGroupType.Field, InputField[]>;

export type InputPlanGroup = TInputGroup<InputGroupType.Plan, InputPlan[]> & {
  selectedPlanId?: string;
};

export type InputButtonGroup = TInputGroup<
  InputGroupType.Button,
  InputButton[]
>;

export type InputButton = InputButtonRegular | InputButtonSubmit;

export type FormState = {
  inputs: Record<string, TInputState>;
  errorMessages: Partial<Record<string, string[]>>;
};

export type Action = {
  type: FormAction;
  payload?: {
    key: string;
    inputState?: TInputState;
    errorMessages?: string[];
    initialState?: FormState;
  };
};

export interface ValidationListType {
  key: string;
  groupKey: string;
  validations: TValidation[];
}

export interface FormProps {
  inputGroups: InputGroup[];

  /** @see CommonFormProps.registrationHackMode */
  registrationHackMode?: boolean;
}

export interface StyledInputProps {
  $hasLeftIcon?: boolean;
  $hasRightIcon?: boolean;
  $touched?: boolean;
  errorMessage?: string;
}

export interface StyledTextAreaLabel {
  $hide: boolean;
}

export type SubmitButtonGroupParams = {
  submit: {
    textKeyPath: string;
    onClick: (data: TFormData, buttonText: string) => Promise<SubmitResponse>;
    disclaimer?: string;
  };
  cancel?: {
    textKeyPath?: string;
    onClick: (data: TFormData, buttonText: string) => void;
  };
  hasSubmitButton: true;
};

export type MainButtonGroupParams = {
  main: {
    textKeyPath: string;
    onClick: (data: TFormData) => void;
    disclaimer?: string;
  };
  cancel?: {
    textKeyPath?: string;
    onClick: () => void;
  };
  hasSubmitButton: false;
};

export type FormGroupButtonProps = {
  isButtonDisabled: (button: InputButton) => boolean;
  activeButtonKey?: string;
  button: InputButton;
  handleSubmit: Function;
  getFormData: Function;
};
